// CanvasResizer.js
//
// Resize the planning canvas WITHOUT losing work. Rebuilding the frame changes
// its origin and scale, so ImageEditor remaps every drawn point through
// old pixel -> UTM -> new pixel when the `frame` prop changes (both frames are
// anchored to the same UTM grid, so the round-trip is exact).
//
// Lives outside ImageEditor deliberately: it is satellite-mode chrome, and
// keeping it here means the shared editor needs no extra UI.

import React, { useState } from 'react';
import { CANVAS_PRESETS } from './KmlSetup';

export default function CanvasResizer({ margin, onApply, canvasW, canvasH, gsd, geometryCount }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(margin);
  const dirty = draft !== margin;

  if (!open) {
    return (
      <button onClick={() => { setDraft(margin); setOpen(true); }} style={pill} title="Resize the planning canvas">
        ⤢ Canvas {Math.round(canvasW)}×{Math.round(canvasH)} m
      </button>
    );
  }

  return (
    <div style={panel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <b style={{ fontSize: 13 }}>Canvas size</b>
        <button onClick={() => setOpen(false)} style={x}>×</button>
      </div>

      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8 }}>
        {CANVAS_PRESETS.map((p) => (
          <button key={p.id} onClick={() => setDraft(p.margin)} title={p.hint}
            style={{ ...chip, background: draft === p.margin ? '#0a84ff' : '#2c2c2e' }}>
            {p.label}
          </button>
        ))}
      </div>

      <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 3 }}>Margin: <b>{draft} m</b></div>
      <input type="range" min="50" max="5000" step="50" value={draft}
        onChange={(e) => setDraft(Number(e.target.value))} style={{ width: '100%' }} />

      <div style={{ fontSize: 11, opacity: 0.6, marginTop: 8, lineHeight: 1.45 }}>
        Now {Math.round(canvasW)} × {Math.round(canvasH)} m at {gsd.toFixed(2)} cm/unit.
        {geometryCount > 0 && <> Your {geometryCount} drawn item{geometryCount === 1 ? '' : 's'} will be
        carried across exactly.</>}
      </div>

      <button
        onClick={() => { onApply(draft); setOpen(false); }}
        disabled={!dirty}
        style={{ ...apply, background: dirty ? '#2e7d32' : '#3a3a3c', cursor: dirty ? 'pointer' : 'default' }}
      >
        {dirty ? 'Resize canvas' : 'No change'}
      </button>
    </div>
  );
}

const base = { fontFamily: 'system-ui, Arial, sans-serif', color: '#f2f2f7' };
const pill = { ...base, position: 'absolute', left: 12, bottom: 12, zIndex: 1200, background: 'rgba(28,28,30,.92)', border: '1px solid #3a3a3c', borderRadius: 999, padding: '7px 13px', fontSize: 12, cursor: 'pointer' };
const panel = { ...base, position: 'absolute', left: 12, bottom: 12, zIndex: 1200, width: 250, background: 'rgba(28,28,30,.97)', border: '1px solid #3a3a3c', borderRadius: 10, padding: 12, boxShadow: '0 10px 30px rgba(0,0,0,.5)' };
const chip = { border: '1px solid #48484a', borderRadius: 999, padding: '3px 9px', color: '#f2f2f7', fontSize: 11, cursor: 'pointer' };
const x = { background: 'transparent', border: 'none', color: '#f2f2f7', fontSize: 17, cursor: 'pointer', lineHeight: 1, padding: 0 };
const apply = { width: '100%', marginTop: 10, border: 'none', borderRadius: 7, padding: '8px 0', color: 'white', fontSize: 12 };
